import { Component, DestroyRef, OnDestroy, OnInit, effect, inject, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import { getErrors, isUserCreated, isUserGettingCreated } from 'src/app/store/createManageUser/selector';
import { ICreateManageUserState } from 'src/app/store/createManageUser/state';
import { isUserName, matchEmail } from 'src/app/utils/validations/field-rules';
import { getSelectedAdminPlan } from 'src/app/login/store/selectors';
import { ILoginState } from 'src/app/login/store/state';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'bmt-create-user',
    templateUrl: './create-user.component.html',
    styleUrls: ['./create-user.component.scss'],
    standalone: false
})
export class CreateUserComponent implements OnInit, OnDestroy {
  luxId = 'BMT-create-user' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);

  createUserForm: UntypedFormGroup;
  readonly createUserErrors = toSignal(this.lux.get(getErrors, this.luxId), { initialValue: '' });
  readonly isUserCreationInProcess = toSignal(this.lux.get(isUserGettingCreated, this.luxId), { initialValue: false });
  private readonly isUserCreatedState = toSignal(this.lux.get(isUserCreated, this.luxId), { initialValue: false });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly isUserGotCreated = signal(false);

  constructor(private readonly router: Router,
              private readonly fb: UntypedFormBuilder,
              private readonly lux: Lux<{ createManageUserState: ICreateManageUserState, login: ILoginState }>,
              private readonly createManageUserAsync: CreateManageUserAsync) {
    effect(() => {
      this.isUserGotCreated.set(this.isUserCreatedState());
    });
  }

  ngOnDestroy(): void {
    this.lux.set(state => state.createManageUserState, { errors: '', userCreated: false });
    this.lux.destroy(this.luxId);
  }

  ngOnInit(): void {
    this.setCreateUserForm();
  }

  setCreateUserForm() {
    this.createUserForm = this.fb.group({
      userIdentifier: ['', [isUserName, Validators.required]],
      lastName: ['', [Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.required, Validators.maxLength(50)]],
      firstName: ['', [Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.required, Validators.maxLength(50)]],
      companyName: ['', [Validators.required, Validators.maxLength(150)]],
      emailAddress: ['', [matchEmail('emailAddress', 'confirmEmail'), Validators.maxLength(100), Validators.pattern("^[A-Z0-9a-z._%+\\-']+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$"), Validators.required]],
      confirmEmail: ['', [matchEmail('emailAddress', 'confirmEmail'), Validators.maxLength(100), Validators.pattern("^[A-Z0-9a-z._%+\\-']+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$"), Validators.required]]
    });
  }

  createUser(): void {
    if (this.createUserForm.valid) {
      delete this.createUserForm.value.confirmEmail;
      const createUserReq = {
        user: { ...this.createUserForm.value, adminPayer: this.selectedAdminPlan() }
      };
      this.lux.set(state => state.createManageUserState.createUserReq, createUserReq);
      this.createManageUserAsync.createUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  manageAuthorizations() {
    this.lux.set(state => state.createManageUserState.manageUser, {
      selectedAction: 'authorizations',
      selectedUserId: this.createUserForm.value.userIdentifier,
      selectedUserType: 'User'
    });
    this.router.navigate(['/manage-user']);
  }

}
